import React from 'react';
import { lastMinuteDealsGradient } from '@/app/common/components/Promotions/styled';
import { TextContainer } from '@/app/common/components/Promotions/LastMinuteDealsBadge/styled';
import { useHotelPromotion } from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import customCss from '@/app/components/hotel-detail/css/customCss';
import styled, { css } from 'styled-components';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { Icon } from '@/app/common/icons/Icon';
import { flashSalesIconSize } from '@/app/common/icons/promotionIcons';
import { useTranslation } from '../translations/Translation';

const lmdTextPreset = css`
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
`;

const EvoContainer = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${lastMinuteDealsGradient};
  cursor: pointer;

  span {
    margin-top: -2px;
  }

  strong::after {
    bottom: -1px;
  }

  > div {
    margin-left: 8px;
  }
`;

export function LmnDealBadge(
  props: { showInfo: boolean; onClick?: () => void } & React.HTMLAttributes<HTMLDivElement>
) {
  const { showInfo, onClick, ...rest } = props;
  const { t } = useTranslation();
  const { showLmnDealsPromotion } = useHotelPromotion();
  const { localization } = usePageWrapperData();
  const { imageBasePath, configValuesApp } = localization;
  const evolutionEnabled = configValuesApp?.evolutionEnabled;
  const icon = `${imageBasePath}icons/last_minute_deals_underline_white.svg`;

  if (evolutionEnabled) {
    return (
      <EvoContainer
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        {...rest}
      >
        <TextContainer
          dangerouslySetInnerHTML={{ __html: t('vacation.oss.last_minute_deals.label_HTML') }}
          $icon={icon}
          $color="#FFFFFF"
          $preset={lmdTextPreset}
        />
        {showInfo && (
          <Icon
            category="Semantics"
            name="icon-circle-info"
            size={16}
            color="iconOnDark"
            alt=""
            css={`
              margin-left: 8px;
            `}
          />
        )}
      </EvoContainer>
    );
  }

  const onBadgeClick = () => {
    if (showInfo) {
      onClick?.();
    }
  };

  return (
    <div
      css={`
        display: flex;
        align-items: center;
        ${showInfo ? 'cursor: pointer;' : undefined}
      `}
      role="button"
      tabIndex={0}
      onClick={onBadgeClick}
      onKeyUp={keyUpHandler(onBadgeClick)}
      {...rest}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          position: relative;
          height: 24px;
          padding: 2px 8px;
          border-radius: ${showInfo ? '4px 0 0 4px' : '4px'};
          gap: 4px;
          color: white;
          background: ${showLmnDealsPromotion ? lastMinuteDealsGradient : customCss.gradients.flashSaleDeal};
        `}
      >
        <Icon
          category="Brands and products"
          name={showLmnDealsPromotion ? 'icon-lastminute-deals' : 'icon-flash-sales'}
          size={showLmnDealsPromotion ? 13 : flashSalesIconSize(13)}
          footprint={13}
          color="iconOnDark"
          alt="lastminute deal"
        />
        <TextContainer
          dangerouslySetInnerHTML={{ __html: t('vacation.oss.last_minute_deals.label_HTML') }}
          $icon={icon}
          $color="#FFFFFF"
          $preset={css`
            ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
          `}
          css={`
            font-size: 12px;
            strong {
              font-size: 14px;
              bottom: 1px;
            }
          `}
        />
      </div>
      {showInfo && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            height: 24px;
            width: 24px;
            border-color: ${(p) => p.theme.commons.colors.gray100};
            border-width: 1px 1px 1px 0;
            border-style: solid;
            border-radius: 0 4px 4px 0;
          `}
        >
          <Icon category="Semantics" name="icon-circle-info" size={16} color="iconPrimary" alt="Lmnd deals info" />
        </div>
      )}
    </div>
  );
}
